<template>
  <!-- round-19 Dev-C: global MT document-number search (#363 §1).
       Type any doc number (SO/MO/DO/PO/IV/receipt PO) -> jump to its page. -->
  <el-autocomplete
    v-model="query"
    :fetch-suggestions="fetchSuggestions"
    :placeholder="$t('modern_trade.doc_search.placeholder')"
    :trigger-on-focus="false"
    value-key="doc_no"
    clearable
    class="w-100 mt-doc-search"
    @select="onSelect"
    @keyup.enter.native="onEnter"
  >
    <i slot="prefix" class="el-input__icon fa fa-search"></i>
    <template slot-scope="{ item }">
      <div class="d-flex justify-content-between align-items-center">
        <span>
          <span class="badge badge-secondary mr-2">{{ typeLabel(item.type) }}</span>
          <strong>{{ item.doc_no }}</strong>
        </span>
        <small class="text-muted" v-if="item.company_name">{{ item.company_name }}</small>
      </div>
    </template>
  </el-autocomplete>
</template>

<script>
import { Autocomplete } from "element-ui";
import swal from "sweetalert2";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtDocSearchBox",
  components: {
    [Autocomplete.name]: Autocomplete,
  },
  data() {
    return {
      query: "",
      lastResults: [],
    };
  },
  methods: {
    // el-autocomplete callback: fetch matches for the current input and hand
    // them to the dropdown. Requires >= 3 chars (mirrors the BE guard).
    async fetchSuggestions(queryString, cb) {
      const q = (queryString || "").trim();
      if (q.length < 3) {
        this.lastResults = [];
        cb([]);
        return;
      }
      try {
        const res = await ModernTradeService.documentSearch(q);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.lastResults = d.items || [];
        cb(this.lastResults);
      } catch (e) {
        this.lastResults = [];
        cb([]);
      }
    },
    // Pressing Enter: single match -> navigate immediately; several -> the
    // dropdown is already open for the user to choose; none -> toast.
    async onEnter() {
      const q = (this.query || "").trim();
      if (q.length < 3) return;
      let items = this.lastResults;
      // If the dropdown hasn't populated yet, fetch once synchronously.
      if (!items.length) {
        try {
          const res = await ModernTradeService.documentSearch(q);
          const d = (res.data && res.data.Data && res.data.Data[0]) || {};
          items = d.items || [];
          this.lastResults = items;
        } catch (e) {
          items = [];
        }
      }
      if (!items.length) {
        swal.fire({ title: this.$t("modern_trade.doc_search.no_result"), icon: "info", timer: 1600, showConfirmButton: false });
        return;
      }
      if (items.length === 1) {
        this.navigate(items[0]);
      }
      // multiple matches: leave the dropdown open for the user to pick.
    },
    onSelect(item) {
      this.navigate(item);
    },
    navigate(item) {
      if (!item || !item.type) return;
      switch (item.type) {
        case "sale_order":
          this.gotoSaleOrder(item.sale_order_id || item.id, "so", item.doc_no);
          break;
        case "po":
          this.gotoSaleOrder(item.sale_order_id || item.id, "po", item.doc_no);
          break;
        case "production_order":
          this.gotoSaleOrder(item.sale_order_id, "mo", item.doc_no);
          break;
        case "delivery":
          this.gotoSaleOrder(item.sale_order_id, "do", item.doc_no);
          break;
        case "invoice":
          this.$router.push({ name: "MtInvoices", query: { invoice_id: String(item.id) } }).catch(() => {});
          break;
        case "receipt":
          this.$router.push({ name: "MtReceiptDetail", params: { id: String(item.receipt_id || item.id) } }).catch(() => {});
          break;
        default:
          break;
      }
      // Clear after navigating so the box is ready for the next lookup.
      this.query = "";
      this.lastResults = [];
    },
    gotoSaleOrder(id, focus, doc) {
      if (!id) return;
      this.$router.push({
        name: "MtSaleOrderDetail",
        params: { id: String(id) },
        query: { focus, doc },
      }).catch(() => {});
    },
    typeLabel(type) {
      const key = "modern_trade.doc_search.type_" + type;
      const label = this.$t(key);
      // Fall back to the raw type when a label is missing.
      return label === key ? type : label;
    },
  },
};
</script>

<style scoped>
.mt-doc-search >>> .el-input__inner {
  padding-left: 30px;
}
</style>
